export default function OfferDiagnosticsDisplay({ diagnostics }) {
    // Safety check for diagnostics object
    if (!diagnostics || typeof diagnostics !== 'object') {
        return (
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-sm text-slate-600">
                    ⚠️ Diagnostic data not available
                </div>
            </div>
        );
    }

    if (diagnostics.overallConfidence >= 100) {
        return (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                <div className="text-sm text-emerald-800 font-medium">
                    ✅ Perfect Match - All components found with high confidence!
                </div>
            </div>
        );
    }

    const getStatusColor = (status) => {
        switch (status) {
            case 'excellent': return 'text-emerald-600 bg-emerald-50 border-emerald-200';
            case 'needs_improvement': return 'text-amber-600 bg-amber-50 border-amber-200';
            case 'missing': return 'text-red-600 bg-red-50 border-red-200';
            default: return 'text-slate-600 bg-slate-50 border-slate-200';
        }
    };

    // Safety checks for all properties
    const confidenceGap = diagnostics.confidenceGap || 0;
    const summary = diagnostics.summary || 'Analysis in progress...';
    const componentBreakdown = diagnostics.componentBreakdown || {};
    const improvementActions = Array.isArray(diagnostics.improvementActions) ? diagnostics.improvementActions : [];
    const whyNot100Percent = Array.isArray(diagnostics.whyNot100Percent) ? diagnostics.whyNot100Percent : [];

    return (
        <div className="space-y-4">
            {/* Summary */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <div className="text-sm font-medium text-slate-800 mb-2">
                    Why not 100%? ({confidenceGap}% confidence gap)
                </div>
                <div className="text-sm text-slate-700">{summary}</div>
            </div>

            {/* Component Breakdown */}
            {Object.keys(componentBreakdown).length > 0 && (
                <div className="space-y-2">
                    <div className="text-sm font-medium text-slate-800">Component Analysis:</div>
                    {Object.entries(componentBreakdown).map(([component, breakdown]) => {
                        // Safety checks for breakdown object
                        const status = breakdown?.status || 'unknown';
                        const confidence = breakdown?.confidence || 0;
                        const pointsLost = breakdown?.pointsLost || 0;
                        const expected = breakdown?.expected;
                        const found = breakdown?.found || false;

                        return (
                            <div key={component} className={`text-xs p-2 rounded border ${getStatusColor(status)}`}>
                                <div className="flex justify-between items-center mb-1">
                                    <span className="font-medium capitalize">{component}</span>
                                    <span className="text-xs">
                                        {confidence}% (-{pointsLost} pts)
                                    </span>
                                </div>
                                <div className="text-xs opacity-80">
                                    Expected: {Array.isArray(expected)
                                        ? expected.slice(0, 2).join(', ') + (expected.length > 2 ? '...' : '')
                                        : expected?.toString().substring(0, 50) || 'N/A'
                                    }
                                </div>
                                {found && (
                                    <div className="text-xs opacity-80 mt-1">
                                        Status: {status === 'excellent' ? '✅ Excellent match' :
                                            status === 'needs_improvement' ? '⚠ Needs improvement' :
                                                '❌ Missing'}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Improvement Actions */}
            {improvementActions.length > 0 && (
                <div className="space-y-2">
                    <div className="text-sm font-medium text-slate-800">Recommended Actions:</div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <ul className="text-sm text-blue-800 space-y-2">
                            {improvementActions.map((action, index) => (
                                <li key={index} className="flex items-start gap-2">
                                    <span className="text-blue-600 font-bold text-xs mt-0.5">{index + 1}.</span>
                                    <span>{typeof action === 'string' ? action : 'Invalid action data'}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            {/* Issues List */}
            {whyNot100Percent.length > 0 && (
                <div className="space-y-2">
                    <div className="text-sm font-medium text-slate-800">Specific Issues:</div>
                    <div className="space-y-1">
                        {whyNot100Percent.map((issue, index) => (
                            <div key={index} className="text-xs bg-red-50 border border-red-200 rounded p-2 text-red-800">
                                • {typeof issue === 'string' ? issue : 'Invalid issue data'}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
